import { Component, OnInit, OnDestroy, DoCheck, Input } from '@angular/core';
import { GfTitleService } from '../../../core/services/core.title.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HttpClient } from '@angular/common/http';
import * as _ from 'lodash';
import { UiService } from '../../../core/services/core.ui.service';
import { TrackeritemService } from '../services/trackeritem.service';
import { TrackerService } from '../services/tracker.service';
import { UtilsService } from '../../../core/services/core.utils.service';
import { TrackeritemQueueService } from '../services/trackeritem.queue.service';
import { UserRest } from '../../../core/services/core.user.rest.service';
import { UserService } from '../../../core/services/core.user.service';
import { NotificationService } from '../../../core/services/core.notification.service';
import { DragNotificationService } from '../../../core/services/core.drag-notification.service';
import { UploadService } from '../../../core/services/core.upload.service';
import { StateService } from '@uirouter/angular';
import { GfTranslateService } from '../../../core/services/core.translate.service';
import { TrackeritemValidationFailuresModalComponent } from '../lib/trackerItemValidationsModal/trackeritem.validations.modal.component';
import { doGetPromise } from '../../../core/config/core.httpclient-utils';
import { WikiService } from '../../../widgets/wiki/services/wiki.service';
import { MentionsService, MentionUser, MentionConfig } from '../../../core/services/core.mentio.service';
import { trackerMentionUsers } from '../services/trackeritem.mentions';

/**
 * Component to handle editing of a tracker item.
 */
@Component({
    selector: 'trackeritem-edit',
    template: require('./ti-edit.main.component.html')
})
export class TrackeritemEditComponent implements OnInit, OnDestroy, DoCheck {
    assignees: any = null;
    mode = '';
    @Input() project: any;
    @Input() projectMenu: any;
    @Input() tracker: any;
    @Input() trackeritem: any;
    @Input() acls: any;

    draggingOver = false;
    temporaryFollowupRefId: string;
    filesUploading: any[] = [];
    maxUploadSize: number;
    followup = '';
    isDetailsEditMode = false;
    errors: any = {};
    currentLockStatus: number;
    trackeritemOriginal: any;
    saveTarget: any;
    assigneeFilter = '';
    tags: any[] = [];
    originalTags: any[] = [];
    tiMerge: any;

    // The @-mention source for the details editor.
    mentions: MentionConfig | null = null;
    currentUser: any;

    pendingApprovalTransitions: any[] = [];
    approvedTransitionCount = 0;
    saveApprovalError = false;
    private _approvalCheckKey = '';

    constructor(
        private httpClient: HttpClient,
        private uiService: UiService,
        private trackerService: TrackerService,
        private trackeritemService: TrackeritemService,
        private utilsService: UtilsService,
        private trackeritemQueueService: TrackeritemQueueService,
        private userRest: UserRest,
        private userService: UserService,
        private notificationService: NotificationService,
        private dragNotificationService: DragNotificationService,
        private uploadService: UploadService,
        private translateService: GfTranslateService,
        private modalService: NgbModal,
        private stateService: StateService,
        private titleService: GfTitleService,
        private mentioService: MentionsService,
        private wikiService: WikiService
    ) { }

    /**
     * Clears the error flag of a field once the user edits it.
     */
    resetErrors(field: string): void {
        if (this.errors && this.errors[field]) {
            this.errors[field].hasError = false;
        }
    }

    /**
     * Initialize the component, setting up necessary properties and subscribing to notifications.
     */
    ngOnInit(): void {
        this.initializeComponent();
        this.setupSubscriptions();
        this.loadTags('trackeritem', this.trackeritem.id);

        this.currentLockStatus = this.trackeritem.lockStatus;
        this.trackeritemOriginal = this.trackeritemService.stripForSave(_.cloneDeep(this.trackeritem));

        // Initialize mention functionality
        this.currentUser = this.userService.session.getUser();
        this.initializeMentionConfigs();

        this.loadNotebookPages();
        this.loadTimeline();
    }

    private loadTimeline(): void {
        this.trackeritemService.fetchTimeline(this.trackeritem.id)
            .then((timelineData: any) => {
                if (!this.trackeritem.rel) { this.trackeritem.rel = {}; }
                ['history', 'messages', 'merge_comments', 'commits', 'associations', 'dependencies', 'duplicates', 'events'].forEach(key => {
                    if (_.has(timelineData, `rel.${key}`)) {
                        _.set(this.trackeritem, `rel.${key}`, timelineData.rel[key]);
                    }
                });
                // rel.* is merged into the existing trackeritem object in place, so children
                // bound via [trackeritem]="trackeritem" (Angular OnChanges only fires on input
                // *reference* changes) won't otherwise notice this data arrived. Broadcast it,
                // same as onApprovalRecorded()'s equivalent refresh.
                this.notificationService.notifyAll('TRACKER_ITEM.EDIT.UPDATED', {
                    trackeritem: this.trackeritem,
                    preventCleanup: true
                });
            })
            .catch(() => { });
    }

    private loadNotebookPages(): void {
        this.wikiService.fetch({ section: 'trackeritem', refId: this.trackeritem.id })
            .then((response: any) => {
                if (!this.trackeritem.rel) { this.trackeritem.rel = {}; }
                this.trackeritem.rel.notebooks = (response && response.items) ? response.items : [];
                this.notificationService.notifyAll('TRACKER_ITEM.EDIT.UPDATED', {
                    trackeritem: this.trackeritem,
                    preventCleanup: true
                });
            })
            .catch(() => { });
    }

    /**
     * Clean up subscriptions when the component is destroyed.
     */
    ngOnDestroy() {
        this.notificationService.unsubscribe('ti-edit');
        this.notificationService.unsubscribe('ti-attachments-tab');
        this.dragNotificationService.destroy();

        this.mentions?.destroy();
    }

    ngDoCheck(): void {
        if (!this.trackeritem || !this.trackeritemOriginal) return;
        const key = this.getApprovalCheckKey();
        if (key !== this._approvalCheckKey) {
            this._approvalCheckKey = key;
            this.recomputeApprovals();
        }
    }

    private getApprovalCheckKey(): string {
        const workflowFields = _.values(_.get(this.tracker, 'rel.workflow.fields', []));
        return workflowFields
            .filter((f: any) => f.useWorkflow)
            .map((f: any) => `${f.alias}:${_.get(this.trackeritem, `extraFields.${f.alias}.fieldData.id`, 0)}`)
            .join(',');
    }

    recomputeApprovals(): void {
        this.pendingApprovalTransitions = [];
        this.approvedTransitionCount = 0;
        this.saveApprovalError = false;
        const workflowFields = _.values(_.get(this.tracker, 'rel.workflow.fields', []));
        for (const field of workflowFields) {
            if (!field.useWorkflow) continue;
            const alias = field.alias;
            const fromId = _.get(this.trackeritemOriginal, `extraFields.${alias}.fieldData.id`);
            const toId   = _.get(this.trackeritem,         `extraFields.${alias}.fieldData.id`);
            if (!fromId || !toId || fromId === toId) continue;
            const fromEl = _.find(field.fieldElements, { id: fromId });
            const toEl   = _.find(_.get(fromEl, 'toElements', []), { id: toId });
            if (toEl && toEl.requiresApproval) {
                this.pendingApprovalTransitions.push({ field, fromEl, toEl });
            }
        }
    }

    onApprovalRecorded(event: any): void {
        this.approvedTransitionCount++;
        this.saveApprovalError = false;
        // Refresh the timeline without resetting trackeritemOriginal — keeping approved panels visible
        this.trackeritemService.fetchTimeline(this.trackeritem.id)
            .then((timelineData: any) => {
                ['history', 'messages', 'merge_comments', 'commits', 'associations', 'dependencies', 'duplicates', 'events'].forEach(key => {
                    if (_.has(timelineData, `rel.${key}`)) {
                        _.set(this.trackeritem, `rel.${key}`, timelineData.rel[key]);
                    }
                });
                this.notificationService.notifyAll('TRACKER_ITEM.EDIT.UPDATED', {
                    trackeritem: this.trackeritem,
                    preventCleanup: true
                });
            });
    }

    /**
     * Initializes the component properties.
     */
    private initializeComponent(): void {
        this.titleService.setTitle(this.project.name, this.tracker.trackerName, '[#' + this.trackeritem.id + '] ' + this.trackeritem.summary);
        this.temporaryFollowupRefId = this.genTemporaryFollowupRefId();
        this.maxUploadSize = this.uploadService.getMaxUploadSize('tracker');
    }

    /**
     * Sets up subscriptions for notifications.
     */
    private setupSubscriptions(): void {
        // Initializes drag notification service for drag and drop
        this.dragNotificationService.initializeDragListeners();
        
        this.notificationService.subscribe('ti-edit', 'DRAGOVER_START_EVENT', () => {
            this.draggingOver = true;
        });

        this.notificationService.subscribe('ti-edit', 'DRAGOVER_END_EVENT', () => {
            this.draggingOver = false;
        });
    }

    /**
     * Toggles edit mode for the details section.
     * @param ev The event object
     */
    toggleEditDetails(ev: Event): void {
        ev.preventDefault();
        ev.stopPropagation();
        this.isDetailsEditMode = !this.isDetailsEditMode;
    }

    /**
     * Receives markdown updates from the details editor. The viewer renders
     * markdown source on the fly, so there's nothing to invalidate here.
     */
    onDetailsChange(details: string): void {
        this.trackeritem.details = details;
        this.trackeritem.detailsFormat = 'text/markdown';
    }

    /**
     * Handles the submission of the tracker item and follow-up data.
     * @param stopRedirection Whether to stop redirection after submission
     * @param setAssigneeOnly Whether to only set the assignee
     * @param source The source of the submission
     */
    doSubmit(stopRedirection: boolean, setAssigneeOnly: boolean, source: any = ''): void {
        if (this.currentLockStatus > 0) {
            this.submitLocked(source);
        } else {
            this.submitUnlocked(stopRedirection, setAssigneeOnly, source);
        }
    }

    /**
     * Handles updates to the timeline.
     * @param event The event object containing the follow-up data
     */
    onTimelineUpdate(event: any): void {
        if (!_.isEmpty(event.followup)) {
            this.trackeritem.followup = event.followup;
            this.trackeritem.followupFormat = event.followupFormat || 'text/markdown';
            this.followup = event.followup;
        }
    }

    /**
     * Refreshes and updates the tracker item data.
     * @param preventCleanup Whether to prevent cleanup after update
     * @param preserveFormValues Whether to preserve current form field values during update
     */
    updateTrackerItem(preventCleanup: boolean = false, preserveFormValues: boolean = false): void {
        const draftFollowup = _.get(this.trackeritem, 'followup', '');

        this.trackeritemService.fetch(this.stateService.params.id, { fetchFull: true })
            .then((trackeritem: any) => {
                this.trackeritem = this.trackeritemService.updateTIValues(this.trackeritem, trackeritem);
                if (_.has(trackeritem, 'rel.attachments')) {
                    this.trackeritem.rel.attachments = trackeritem.rel.attachments;
                }
                this.trackeritemOriginal = this.trackeritemService.stripForSave(_.cloneDeep(this.trackeritem));

                this.currentLockStatus = trackeritem.lockStatus;
                if (preserveFormValues) {
                    this.followup = draftFollowup;
                    this.trackeritem.followup = draftFollowup;
                    this.trackeritem.followupFormat = 'text/markdown';
                } else {
                    this.followup = "";
                    this.trackeritem.followup = "";
                    this.trackeritem.followupFormat = '';
                }

                if (!preserveFormValues) {
                    this.errors = {};
                }

                this.notificationService.notifyAll('TRACKER_ITEM.EDIT.UPDATED', { 
                    trackeritem: this.trackeritem, 
                    preventCleanup: preventCleanup 
                });
            });
    }


    /**
     * Handles the unlocked submission of the tracker item and follow-up data.
     * @param stopRedirection Whether to stop redirection after submission
     * @param setAssigneeOnly Whether to only set the assignee
     * @param source The source of the submission
     */
    submitUnlocked(stopRedirection: boolean, setAssigneeOnly: boolean, source: string) {
        if (this.acls.canUpdate || this.acls.isAdmin) {
            this.trackeritem.tags = this.tags.map(tag => tag.text).join(',');

            const followupData = {
                message: this.trackeritem.followup,
                refId: this.temporaryFollowupRefId
            };

            this.saveTarget = source;

            this.save(this.trackeritem, this.trackeritemOriginal, this.tracker, followupData, {
                stopRedirection: stopRedirection,
                setAssigneeOnly: setAssigneeOnly,
                followupOnly: !this.acls.canUpdate
            })
                .then(() => {
                    this.saveSuccessCallback(stopRedirection);
                }, (errors) => {
                    if (errors && (errors.blocked?.blockedError || errors.overwrite?.overwriteError || !_.isEmpty(errors.fields) || errors.approvalRequired)) {
                        this.saveErrorCallback(errors);
                    } else {
                        this.saveSuccessCallback(stopRedirection);
                    }
                })
                .then(() => {
                    this.saveTarget = false;
                });
        } else if (this.acls.canFollowup && !_.isEmpty(this.trackeritem.followup)) {
            this.submitLocked(source);
        }
    }


    /**
     * Handles the locked submission of the tracker item and follow-up data.
     * @param source The source of the submission
     */
    submitLocked(source: string) {
        const lockStatusChanged = this.currentLockStatus !== this.trackeritem.lockStatus;
        const allowedFollowup = this.acls.canFollowup && this.trackeritem.followup !== '';

        if (!lockStatusChanged && !allowedFollowup) {
            const warningMsg = this.translateService.translate('There are no changes to save');
            this.uiService.message.showWarning(warningMsg, 'Tracker Item');
            return;
        }

        let trackeritemData = null;
        let followupData = null;

        if ((this.currentLockStatus !== this.trackeritem.lockStatus) && (this.acls.canUpdate || this.acls.isAdmin)) {
            trackeritemData = {
                id: this.trackeritem.id,
                lockStatus: this.trackeritem.lockStatus,
                lastModifiedDate: this.trackeritem.lastModifiedDate
            };
        }

        if ((this.currentLockStatus !== 1) && this.trackeritem.followup !== '' && this.acls.canFollowup) {
            followupData = {
                message: this.trackeritem.followup,
                refId: this.temporaryFollowupRefId
            };
        }

        if (trackeritemData) {
            this.saveTarget = source;

            this.save(trackeritemData, this.trackeritemOriginal, this.tracker, followupData, { stopRedirection: true, setAssigneeOnly: false, followupOnly: false })
                .then((errors) => {
                    if (_.get(errors, 'overwrite.overwriteError', false) || !_.isEmpty(_.get(errors, 'fields', {}))) {
                        this.saveErrorCallback(errors);
                    } else {
                        this.saveSuccessCallback(true);
                    }
                }, (errors) => {
                    this.saveErrorCallback(errors);
                })
                .then(() => {
                    this.saveTarget = false;
                });
        } else if (followupData) {
            this.saveTarget = source;

            this.trackeritemService.followupSave(followupData.message, followupData.refId, this.trackeritem.id, _.get(this.userService.getUser(), 'id', 100))
                .then(() => {
                    this.updateTrackerItem(false, true);
                }, (err) => { })
                .then(() => {
                    this.saveTarget = false;
                });
        }
    }

    /**
     * Cancels the current operation and navigates back to the tracker.
     * @param e The event object
     */
    doCancel(e: Event): void {
        if (e) {
            e.preventDefault();
        }
        this.trackerService.goToTracker(this.tracker);
    }

    /**
     * Generates a temporary follow-up reference ID.
     * @returns A string representing the temporary follow-up reference ID
     */
    genTemporaryFollowupRefId(): string {
        return `${this.trackeritem.id}${_.random(1, 9999)}`;
    }

    /**
     * Refreshes the tracker item by navigating to the current route.
     */
    refreshTrackerItem(): void {
        this.stateService.go('home.project.summary.tracker.item.edit', this.stateService.params,
            { inherit: true, notify: true, location: true, reload: true });
    }

    /**
     * Callback function when merge info is ready.
     * @param tiMerge The merge information
     */
    tiMergeInfoReadyCallback(tiMerge: any): void {
        this.tiMerge = tiMerge;
    }

    /**
     * Handles the save event and submits the tracker item data.
     * @param params The parameters for submission
     */
    onSave(params: any): void {
        this.doSubmit(params.stopRedirection, params.setAssigneeOnly, params.source);
    }

    /**
     * Handles successful save operation and decides the next steps.
     * @param stopRedirection Whether to stop redirection after submission
     */
    private saveSuccessCallback(stopRedirection: boolean): void {
        this.errors = {};
        this.temporaryFollowupRefId = this.genTemporaryFollowupRefId();
        this.assigneeFilter = '';
        const warningMessage = this.translateService.translate('Your queue is empty - no more Tracker Items to show');

        const itemInQueue = this.trackeritemQueueService.inQueue(this.trackeritem);

        if (!this.trackeritemService.isAssignedTo(this.trackeritem, this.userService.session.getUser()) && itemInQueue) {
            this.trackeritemQueueService.removeTrackerItemFromQueue(this.trackeritem);
        }

        if (!stopRedirection && itemInQueue) {
            const nextId = this.trackeritemQueueService.getNextId(this.trackeritem.id);
            if (nextId) {
                this.stateService.go('home.trackeritem', { id: nextId });
                return;
            }
            this.uiService.message.showWarning(warningMessage, this.translateService.translate('Empty Queue'));
            this.stateService.go('home.user.my', { unixName: this.userService.session.getUnixName() });
            return;
        } else if (!stopRedirection) {
            this.trackerService.goToTracker(this.tracker);
        } else {
            // Updates the current lock status to match the saved value
            this.currentLockStatus = this.trackeritem.lockStatus;
            this.updateTrackerItem(false, false);
        }
    }

    /**
     * Handles errors during the save operation and displays appropriate messages.
     * @param errors The error object
     */
    private saveErrorCallback(errors: any): void {
        if (errors?.approvalRequired) {
            this.saveApprovalError = true;
            const msg = this.translateService.translate('An approval is required for this transition. Please record all approvals before saving.');
            this.uiService.message.showError(msg, 'Tracker Item');
            return;
        }

        if (errors?.blocked?.blockedError) {
            this.uiService.message.showWarning(errors.blocked.blockedMessage, 'Tracker Item');
            return;
        }

        this.errors = _.get(errors, 'fields', {});
        const warningMsg = this.translateService.translate('Sorry, could not update this Tracker Item');
        this.uiService.message.showWarning(warningMsg, 'Tracker Item');
        setTimeout(() => {
            this.openValidationFailuresModal();
        });
    }

    /**
     * Performs a submission operation without redirection.
     */
    saveItem(): void {
        this.doSubmit(true, false);
    }

    /**
     * Opens a modal to display validation failures.
     */
    openValidationFailuresModal(): void {
        const modalRef = this.modalService.open(TrackeritemValidationFailuresModalComponent);
        modalRef.componentInstance.trackeritem = this.trackeritem;
        modalRef.componentInstance.tracker = this.tracker;
        modalRef.componentInstance.tags = this.tags;
        modalRef.componentInstance.errors = this.errors;
        modalRef.componentInstance.onUpdate.subscribe(($event) => {
            modalRef.close();
            this.saveItem();
        });
        modalRef.componentInstance.close.subscribe(($event) => {
            modalRef.close();
        });
    }

    /**
     * Checks if the follow-up message is new.
     * @param followup The follow-up object
     * @returns True if the follow-up message is new, false otherwise
     */
    public isNewFollowup(followup: any): boolean {
        const message = _.get(followup, 'message', '');

        // Remove all HTML tags and to check for text content
        let textContent = message.replace(/<[^>]*>/g, '').trim();

        // Remove all HTML entities like &nbsp;, &amp;, etc.
        textContent = textContent.replace(/&[^;]+;/g, '');

        // Remove all whitespace characters (spaces, tabs, newlines)
        textContent = textContent.replace(/\s/g, '');

        // If there is non-empty text content, returns true
        if (textContent.length > 0) {
            return true;
        }

        // If not, check for any tags as images, videos, etc
        const contentTags = [
            'img',
            'video',
            'audio',
            'svg',
            'picture',
            'source',
        ];

        const contentTagsRegex = new RegExp(
            `<\\s*(${contentTags.join('|')})(\\s|>)`,
            'i'
        );

        if (contentTagsRegex.test(message)) {
            return true;
        }

        return false;
    }

    fileSelectedOnTimeline(event: any) {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.processFiles(input.files);
        }
    }

    /**
     * Handles file selection and initiates the upload process.
     * @param $files The selected files
     * @param $event The event object
     */
    onDragOver(event: DragEvent): void {
        event.preventDefault();

        // This ensures the drop zone shows up immediately
        if (!this.draggingOver) {
            this.draggingOver = true;
        }
    }

    onDragLeave(event: DragEvent): void {
        // Hide if we're leaving the main container
        const rect = (event.currentTarget as Element).getBoundingClientRect();
        const x = event.clientX;
        const y = event.clientY;
        
        // Checks if the mouse is outside the container
        if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
            this.draggingOver = false;
        }
    }

    fileSelected(event: DragEvent): void {
        event.preventDefault();
        
        // Resets drag state immediately when drop occurs
        this.draggingOver = false;
        
        if (event.dataTransfer && event.dataTransfer.files) {
            this.processFiles(event.dataTransfer.files);
        }
    }

    processFiles(files: any) {
        if (files) {
            const options = {
                successCallback: this.setAttachment.bind(this),
                progressCallback: this.progressCallback.bind(this),
                finishedCallback: this.removeFileFromInProgress.bind(this)
            };

            this.addFilesToUploading(files);
            let filesToUpload: any = Array.from(files);
            this.uploadService.uploadFiles('trackeritem', this.trackeritem.id, filesToUpload, options)
                .then(() => {
                    this.updateTrackerItem(true, true);
                })
                .catch(() => { });
        }
    }

    /**
     * Sets attachment for the tracker item after successful upload.
     * @param section The section identifier
     * @param refId The reference ID
     * @param file The file object
     * @param hash The file hash
     * @returns A promise with the set attachment result
     */
    setAttachment(section: string, refId: number, file: any, hash: any): Promise<any> {
        const params = {
            fileName: file.name,
            fileType: file.type || 'application/octet-stream'
        };

        return this.trackeritemService.setAttachment(this.trackeritem.id, hash, params)
            .then((response: any) => {
                this.appendAttachment(response);

                if (_.has(response, 'lastModifiedDate')) {
                    this.trackeritem.lastModifiedDate = response.lastModifiedDate;
                }
            });
    }

    /**
     * Adds selected files to the uploading list.
     * @param files The selected files
     */
    private addFilesToUploading(files: FileList): void {
        Array.from(files).forEach(file => {
            this.filesUploading.push({
                name: file.name,
                size: file.size,
                bytesUploaded: 0,
                bytesRemaining: file.size,
                percentageUploaded: 0,
                percentageRemaining: 100
            });
        });
    }

    /**
     * Removes file from the uploading list after upload is finished.
     * @param section The section identifier
     * @param refId The reference ID
     * @param file The file object
     */
    private removeFileFromInProgress(section: string, refId: number, file: any): void {
        _.remove(this.filesUploading, (_file: any) => _file.name === file.name);
    }

    /**
     * Callback function for file upload progress updates.
     * @param section The section identifier
     * @param refId The reference ID
     * @param file The file object
     * @param bytesUploaded The number of bytes uploaded
     * @param bytesRemaining The number of bytes remaining
     * @param percentUploaded The percentage of bytes uploaded
     * @param percentRemaining The percentage of bytes remaining
     */
    private progressCallback(section: string, refId: number, file: any, bytesUploaded: number, bytesRemaining: number, percentUploaded: number, percentRemaining: number): void {
        const fileInProgress = _.find(this.filesUploading, { name: file.name });

        if (fileInProgress) {
            fileInProgress.amountComplete = bytesUploaded;
            fileInProgress.amountTotal = bytesRemaining;
            fileInProgress.percentComplete = percentUploaded;
            fileInProgress.percentRemaining = percentRemaining;
        }
    }

    /**
     * Creates a temporary history entry for appended attachment.
     * @param attachment The attachment object
     * @returns The temporary history entry
     */
    private createTempHistoryForAppendAttachment(attachment: any): any {
        return {
            activityOrigin: 'audit_trail',
            activityDate: new Date().toISOString(),
            user: attachment.postedBy,
            data: [{
                fieldname: 'attachment_Added',
                oldvalue: 'None',
                newvalue: attachment.fileName
            }],
            activityType: 'update'
        };
    }

    /**
     * Loads attachment data from the specified URL.
     * @param url The URL to fetch the attachment data
     * @returns A promise with the attachment data
     */
    loadAttachment(url: string): Promise<any> {
        return doGetPromise(this.httpClient, url)
            .then((response: any) => ({
                success: true,
                attachment: response
            }))
            .catch(() => ({
                success: false
            }));
    }

    /**
     * Creates a temporary history entry for deleted attachment.
     * @param attachment The attachment object
     * @returns The temporary history entry
     */
    private createTempHistoryForDeleteAttachment(attachment: any): any {
        return {
            activityOrigin: 'audit_trail',
            activityDate: new Date().toISOString(),
            user: attachment.postedBy,
            data: [{
                fieldname: 'attachment_Removed',
                oldvalue: attachment.fileName,
                newvalue: 'None'
            }],
            activityType: 'update'
        };
    }

    /**
     * Appends the attachment to the tracker item.
     * @param attachmentLocation The location of the attachment
     */
    private appendAttachment(attachmentLocation: any): void {
        if (!_.has(attachmentLocation, 'location')) {
            return;
        }

        this.loadAttachment(attachmentLocation.location)
            .then((response: any) => {
                if (response.success && _.has(response, 'attachment')) {
                    if (!_.some(this.trackeritem.rel.attachments, { id: response.attachment.id })) {
                        this.trackeritem.rel.attachments.unshift(response.attachment);
                        this.trackeritem.rel.history.unshift(this.createTempHistoryForAppendAttachment(response.attachment));
                        this.notificationService.notifyAll('TRACKER_ITEM.EDIT.UPDATED', {
                            trackeritem: this.trackeritem,
                            preventCleanup: true
                        });
                    }
                }
            });
    }

    /**
     * Loads tags for the specified section and reference ID.
     * @param section The section identifier
     * @param refId The reference ID
     */
    loadTags(section: string, refId: number): void {
        doGetPromise(this.httpClient, `/api/tag/${section}/${refId}`)
            .then((response: any) => {
                this.tags = _.map(_.get(response, 'items', []), (t: any) => ({
                    text: t.tagName,
                    bgcolor: this.uiService.color.stringToClassColor(t.tagName)
                }));
                this.originalTags = this.tags;
            })
            .catch(() => {
                console.log('gfTagsDisplay fetch failed');
                this.tags = [];
            });
    }

    /**
     * Saves tags for the specified section and reference ID.
     * @param tags The tags to save
     * @param section The section identifier
     * @param refId The reference ID
     */
    saveTags(tags: any[], section: string, refId: number): void {
        const label = `[#${ refId}]`;
        this.httpClient.put(`/api/tag/${section}/${refId}`, {
            tags: _.map(tags, 'text').join(',')
        })
            .toPromise()
            .then(() => {
                this.uiService.message.showSuccess(this.translateService.translate('Updated tags for {{label}}', {
                    label: label
                }), this.translateService.translate('Save Tracker Item'));
                this.originalTags = tags;
            })
            .catch((response: any) => {
                console.log('saveTags fail', response);
                this.uiService.message.showWarning(this.translateService.translate('Sorry, could not update tags for {{label}}', {
                    label: label
                }), this.translateService.translate('Save Tracker Item'));
            });
    }

    private isTrackerItemUpdated(trackeritem: any, trackeritemOriginal: any): Boolean {
        const toCheck = this.trackeritemService.stripForSave(_.cloneDeep(trackeritem));
        // trackeritemOriginal is already trackeritemService.stripForSave'd
        return !this.utilsService.compareObjects(
            toCheck,
            trackeritemOriginal
        );
    }

    save(trackeritem: any, trackerItemBase: any, tracker: any, followup: any, options: any): Promise<any> {
        if (!this.isTrackerItemUpdated(trackeritem, trackerItemBase) && !this.isNewFollowup(followup)) {
            this.uiService.message.showSuccess(this.translateService.translate("There are no changes to save"), "Tracker item");
            return Promise.reject('No changes to save');
        }

        options = options || {};
        options.stopRedirection = options.stopRedirection !== undefined ? options.stopRedirection : false;
        options.setAssigneeOnly = options.setAssigneeOnly !== undefined ? options.setAssigneeOnly : false;
        options.followupOnly = options.followupOnly !== undefined ? options.followupOnly : false;

        const params = {
            setAssigneeOnly: options.setAssigneeOnly
        };

        const errors: any = {
            fields: {},
            overwrite: {
                overwriteError: false,
                overwriteMessage: ''
            },
            blocked: {
                blockedError: false,
                blockedMessage: ''
            }
        };

        const _trackeritem = _.cloneDeep(trackeritem);

        if (this.isNewFollowup(followup)) {
            _trackeritem.followupRefId = followup.refId;
        } else {
            _trackeritem.followup = "";
        }

        if (this.isTrackerItemUpdated(trackeritem, trackerItemBase) || this.isNewFollowup(followup)) {
            return this.trackeritemService.save(_trackeritem, params)
                .then((response: any) => {
                    trackeritem.lastModifiedDate = _.get(response, 'lastModifiedDate', trackeritem.lastModifiedDate);
                    this.trackeritemOriginal = _.cloneDeep(trackeritem);
                    this.followup = '';
                    this.trackeritem.followup = '';
                    this.trackeritem.followupFormat = '';

                    // The response from API POST to save the TI already includes the assignees rel
                    if (_.has(response, 'rel.assignees')) {
                        _.set(trackeritem, 'rel.assignees', response.rel.assignees);
                    }

                    if (options.stopRedirection) {
                        return doGetPromise(this.httpClient, `/api/trackeritem/${trackeritem.id}`, { rel: "history,messages" })
                            .then((response: any) => {
                                if (_.has(response, 'rel.history')) {
                                    trackeritem.rel.history = response.rel.history;
                                }
                                if (_.has(response, 'rel.messages')) {
                                    trackeritem.rel.messages = response.rel.messages;
                                }
                            });
                    }
                })
                .then(() => {
                    this.notificationService.notifyAll('TRACKER_ITEM.EDIT.UPDATED', {
                        trackeritem: trackeritem,
                        tracker: tracker,
                        preventCleanup: false
                    });

                    if (_.get(this, 'tags', []) !== _.get(this, 'originalTags', [])) {
                        this.saveTags(this.tags, 'trackeritem', this.trackeritem.id);
                    }
                })
                .catch((response: any) => {
                    if (_.has(response, 'overwrite')) {
                        if (_.has(response, 'fields')) {
                            errors.fields = response.fields;
                        }

                        errors.overwrite.overwriteError = true;

                        if (_.has(response.overwrite, 'overwrite') && (response.overwrite.overwrite !== null)) {
                            return this.userRest.getBasic(response.overwrite.overwrite)
                                .then((user: any) => {
                                    errors.overwrite.overwriteMessage = this.translateService.translate('The tracker item has already been updated by user {{unixName}}.', { unixName: user.unixName });

                                    this.uiService.message.showError(
                                        this.translateService.translate('Tracker item {{tid}} was previously updated by user {{unixName}}', { tid: trackeritem.id, unixName: user.unixName }),
                                        this.translateService.translate('Tracker item overwrite'),
                                        { user: user }
                                    );
                                });
                        }
                    } else if (_.has(response, 'blocked')) {
                        errors.blocked.blockedError = true;
                        errors.blocked.blockedMessage = this.translateService.translate('The tracker item is blocked by the following open tracker items: ' + _.get(response, 'blocked.tis', '') + ', and cannot be closed.');
                    } else if (_.has(response, 'approval-required')) {
                        errors.approvalRequired = true;
                    } else {
                        errors.fields = response;
                    }

                    return Promise.reject(errors);
                });
        }
    }

    /**
     * Initializes mention configuration for the details field
     */
    initializeMentionConfigs(): void {
        this.mentions = this.mentioService.createMentionConfig({
            project: () => this.project?.unixName || '',
            seed: () => this.getTrackerUsers(),
            roles: () => this.project?.rel?.roles || []
        });
    }

    /**
     * Gets all users related to the tracker item
     * @returns Array of users related to the tracker item
     */
    getTrackerUsers(): MentionUser[] {
        return trackerMentionUsers(this.mentioService, this.tracker, this.trackeritem);
    }

}

